/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Stack } from '../../../design-system/layout/Stack';
import { Grid } from '../../../design-system/layout/Grid';
import { Cluster } from '../../../design-system/layout/Cluster';
import { Button } from '../../../design-system/atoms/Button/Button';
import { CodeBlock } from '../../../design-system/atoms/CodeBlock/CodeBlock';
import { PanelCard } from '../../../design-system/molecules/PanelCard/PanelCard';
import { KeyValueList } from '../../../design-system/molecules/KeyValueList/KeyValueList';
import { JobSummaryCard } from '../../organisms/JobSummaryCard/JobSummaryCard';
import { JobTimeline } from '../../organisms/JobTimeline/JobTimeline';
import { JobErrorBox } from '../../organisms/JobErrorBox/JobErrorBox';
import { StageView } from '../../organisms/StageView/StageView';
import type { StageColumnGroup, StageRun } from '../../organisms/StageView/StageView';
import { theme } from '../../../design-system/theme/theme';
import type { JobDetailData, TimelineStep } from '../../types';

export interface JobDetailScreenProps {
  job: JobDetailData;
  onBack: () => void;
  /** 이 Job의 원본 스냅샷이 존재하는지. true일 때만 "원본 스냅샷" 버튼을 렌더한다. */
  snapshotAvailable?: boolean;
  onOpenSnapshot?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  /** 다른 Job 상세로 이동 (RETRY_SUCCESS일 때 성공한 재시도 Job 링크). */
  onOpenJob?: (id: number) => void;
  /** 스테이지 뷰 (실 API: /jobs/{id}/stages). 이 Job 한 건. */
  stageColumns: StageColumnGroup[];
  stageRuns: StageRun[];
}

const monoStyle = css`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
`;

const collectorLineStyle = css`
  margin-bottom: ${theme.space.xs}px;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};

  span {
    color: ${theme.color.text};
  }
`;

const footNoteStyle = css`
  margin: 0;
  font-size: ${theme.fontSize.sm}px;
  color: ${theme.color.textMuted};

  em {
    font-style: normal;
    color: ${theme.color.danger};
  }
`;

/** 생성→시작→종료 단계 구성. 시안 step 렌더 규칙에서 추출. */
function buildSteps(job: JobDetailData): TimelineStep[] {
  return [
    { label: '생성(QUEUED)', time: job.created, done: true },
    { label: '시작(RUNNING)', time: job.started, done: job.started !== null },
    {
      label: job.status === 'FAILED' ? '종료(FAILED)' : '종료(SUCCESS)',
      time: job.finished,
      done: job.finished !== null,
    },
  ];
}

/** Job 상세 화면. 드릴다운 전용 — 요약·타임라인·결과·입력·에러. */
export function JobDetailScreen({
  job,
  onBack,
  snapshotAvailable,
  onOpenSnapshot,
  onCancel,
  onRetry,
  onOpenJob,
  stageColumns,
  stageRuns,
}: JobDetailScreenProps) {
  return (
    <Stack gap="md">
      <Cluster justify="between">
        <Button variant="text" tone="neutral" onClick={onBack}>
          ← 수집 이력으로
        </Button>
        <Cluster gap="sm">
          {snapshotAvailable && (
            <Button variant="outline" tone="neutral" onClick={onOpenSnapshot}>
              원본 스냅샷
            </Button>
          )}
          {job.status === 'RETRY_SUCCESS' ? (
            // 재시도로 성공한 Job — 정방향 재시도 Job(retryJobId) 상세로 이동하는 링크.
            <Button
              variant="outline"
              onClick={
                job.latestRetryId !== null && onOpenJob
                  ? () => onOpenJob(job.latestRetryId as number)
                  : undefined
              }
              disabled={job.latestRetryId === null || !onOpenJob}
            >
              재시도 결과 · #{job.latestRetryId} →
            </Button>
          ) : (
            // FAILED만 재시도 실행 활성화. RUNNING·SUCCESS·QUEUED는 비활성.
            <Button onClick={onRetry} disabled={job.status !== 'FAILED'}>
              재시도 실행
            </Button>
          )}
        </Cluster>
      </Cluster>

      <JobSummaryCard job={job} />

      <JobTimeline
        steps={buildSteps(job)}
        footnote={`대기 ${job.started ? '~2s' : '—'} · 실행 ${job.finished ? '약 2~3분' : '진행 중'}`}
      />

      <StageView
        columns={stageColumns}
        runs={stageRuns}
        avgRunLabel={`Job #${job.id}`}
      />

      <Grid cols={2} gap="md">
        <PanelCard title="결과">
          <KeyValueList
            items={[
              { key: 'posts', value: job.posts ?? '—' },
              { key: 'comments', value: job.comments?.toLocaleString() ?? '—' },
              { key: 'collection_id', value: <span css={monoStyle}>{job.collectionId ?? '—'}</span> },
            ]}
          />
        </PanelCard>
        <PanelCard title="입력" headerRight={<span css={footNoteStyle}>실제 적용값</span>}>
          <div css={collectorLineStyle}>
            collector_type: <span css={monoStyle}>{job.collector}</span>
          </div>
          <CodeBlock>{job.inputJson}</CodeBlock>
        </PanelCard>
      </Grid>

      {job.status === 'FAILED' && job.errorText !== null && (
        <JobErrorBox errorText={job.errorText} hint="ingest_job.error" />
      )}

      <p css={footNoteStyle}>
        실행 로그는 저장하지 않습니다. 실패 시 위 <em>에러</em> 박스에 원인(ingest_job.error)만 표시됩니다.
      </p>
      {/* TODO: 원본 스냅샷·실행 취소·재시도 액션 API 연결 */}
    </Stack>
  );
}
